// Grouped client for Organization operations
import { ApiConfiguration } from '../ApiConfiguration';
import { Form } from '../models/Form';
import { Organization } from '../models/Organization';
import { FilterOrganizationRequest } from '../models/FilterOrganizationRequest';
import { DataviewOrganizationResponse } from '../models/DataviewOrganizationResponse';
import { FormSerializer } from '../serializers/FormSerializer';
import { OrganizationSerializer } from '../serializers/OrganizationSerializer';
import { FilterOrganizationRequestSerializer } from '../serializers/FilterOrganizationRequestSerializer';
import { DataviewOrganizationResponseSerializer } from '../serializers/DataviewOrganizationResponseSerializer';

/**
 * HTTP client for Organization operations
 *
 * Client for Organization operations
 */
export class OrganizationClient {
  private readonly config: ApiConfiguration;

  constructor(config: ApiConfiguration) {
    this.config = config;
  }

  async getFormForOrganization(formName: string, objectId: string, description?: string | null, email?: string | null, phone?: string | null, timeZoneId?: string | null, t?: string | null, name?: string | null, fciInBranchId?: string | null, id?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/Organization({objectId})/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const queryParams = new URLSearchParams();
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (email !== undefined) {
      queryParams.append('Email', String(email));
    }
    if (phone !== undefined) {
      queryParams.append('Phone', String(phone));
    }
    if (timeZoneId !== undefined) {
      queryParams.append('TimeZoneId', String(timeZoneId));
    }
    if (t !== undefined) {
      queryParams.append('_t', String(t));
    }
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (fciInBranchId !== undefined) {
      queryParams.append('FCI|INBranchId', String(fciInBranchId));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async getFormForTypeOrganization(formName: string, description?: string | null, email?: string | null, phone?: string | null, timeZoneId?: string | null, t?: string | null, name?: string | null, fciInBranchId?: string | null, id?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/Organization/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      ;

    const queryParams = new URLSearchParams();
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (email !== undefined) {
      queryParams.append('Email', String(email));
    }
    if (phone !== undefined) {
      queryParams.append('Phone', String(phone));
    }
    if (timeZoneId !== undefined) {
      queryParams.append('TimeZoneId', String(timeZoneId));
    }
    if (t !== undefined) {
      queryParams.append('_t', String(t));
    }
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (fciInBranchId !== undefined) {
      queryParams.append('FCI|INBranchId', String(fciInBranchId));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async filterOrganization(request?: FilterOrganizationRequest | null, options?: { headers?: Record<string, string> }): Promise<Organization[]> {
    const path = '/app/api/Object/Organization/Filter';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterOrganizationRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => OrganizationSerializer.fromObject(item));
  }

  async recentOrganization(request?: FilterOrganizationRequest | null, options?: { headers?: Record<string, string> }): Promise<Organization[]> {
    const path = '/app/api/Object/Organization/Recent';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterOrganizationRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => OrganizationSerializer.fromObject(item));
  }

  async dataViewOrganization(request?: FilterOrganizationRequest | null, options?: { headers?: Record<string, string> }): Promise<DataviewOrganizationResponse> {
    const path = '/app/api/Object/Organization/DataView';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterOrganizationRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataviewOrganizationResponseSerializer.fromObject(data);
  }

}
